import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Banner from 'components/Banner';
import { motion } from 'framer-motion';
import { UseQueryResult } from 'hooks/useQuery';
import { useTranslation } from 'services/localization';

export interface Props<T> extends UseQueryResult<T> {}
const QueryResultsLimitedBanner = <T extends object>(props: Props<T>) => {
	const { t } = useTranslation();
	const { count, limit, loading, initialLimit, setLimit } = props;

	const [hidden, setHidden] = useState<boolean>(false);

	const isVisible = !hidden && (count >= limit || limit > initialLimit);
	const isAtLimit = !loading && count < limit;
	const canLoadMore = count >= limit;

	useEffect(() => {
		setHidden(false);
	}, [limit]);

	const handleLoadMore = () => {
		setLimit((prev) => prev + initialLimit);
	};
	return (
		<motion.div
			style={{ overflow: 'hidden' }}
			initial={{
				height: 0,
			}}
			animate={isVisible ? 'visible' : 'hidden'}
			variants={{
				visible: {
					height: 'auto',
					transition: {
						delay: 0.3,
					},
				},
				hidden: {
					height: 0,
				},
			}}
		>
			<Box sx={{ mt: 2 }}>
				<Banner
					variant="info"
					title={t('rentals.queryResultsLimitedBanner.title', 'Results limited')}
					description={
						!isAtLimit
							? t('rentals.queryResultsLimitedBanner.description1', {
									count: count,
									defaultValue: 'Showing first {{count}} results',
							  })
							: t('rentals.queryResultsLimitedBanner.description2', 'All results loaded')
					}
					button={
						canLoadMore
							? {
									text: t('common:actions.loadMore', 'Load more'),
									onClick: handleLoadMore,
									loading: loading,
							  }
							: isAtLimit && !hidden
							? {
									text: t('common:greetings.okBtn', 'OK'),
									onClick: () => setHidden(true),
							  }
							: undefined
					}
				/>
			</Box>
		</motion.div>
	);
};

export default QueryResultsLimitedBanner;
